import { isSubscriptionInterval, SubscriptionInterval } from 'constants/order';
import { isSubscriptionItem } from 'interfaces/line-item';
import { Gtm } from 'tracking';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { useCart } from 'contexts/cart';

/**
 * Add items to cart by url params
 * url params: add=variantId,quantity,interval
 * @example ?add=1234567890,2,2week
 */
export const useAddItemsByUrl = () => {
  const { effectiveCart, isCartReady, addToCart } = useCart();
  const { isReady, query, replace, pathname, asPath } = useRouter();
  const { add, addSingle, interval: intervalParam } = query;
  const addItemsByUrlPerformed = useRef(false);
  const addedVariantIds = useRef<
    Array<{
      variantId: string;
      quantity: number;
      interval?: SubscriptionInterval;
    }>
  >([]);
  const [addedItems, setAddedItems] = useState<
    {
      productId: string;
      productTitle: string;
      variantTitle: string;
      variantId: string;
      productType: string;
    }[]
  >([]);

  /**
   * Add items to cart by url params
   */
  useEffect(() => {
    const addParam = add || [];
    const addItems = Array.isArray(addParam) ? addParam : [addParam];
    const variantId = addSingle as string;
    const interval = intervalParam as SubscriptionInterval;

    if (
      !isReady ||
      addItemsByUrlPerformed.current ||
      !isCartReady ||
      (addItems.length === 0 && !variantId)
    ) {
      return;
    }

    // Keep this URL consumed until the router has removed its add parameters.
    // Cart updates and tracking can finish before the shallow replace does.
    addItemsByUrlPerformed.current = true;

    const productsToAdd: {
      variantId: string;
      quantity: number;
      interval?: SubscriptionInterval;
    }[] = [];

    // Handle legacy format: add=variantId,quantity,interval
    if (addItems?.length > 0) {
      productsToAdd.push(
        ...addItems.map((value) => {
          const [variantId, quantity = '1', interval] = value.split(',');
          const item = {
            variantId,
            quantity: parseInt(quantity, 10),
            interval: interval as SubscriptionInterval,
          };
          addedVariantIds.current.push(item);
          return item;
        })
      );
    }

    // Handle new format: addSingle=variantId&interval=interval
    if (addSingle) {
      const item = {
        variantId,
        quantity: 1,
        interval,
      };
      addedVariantIds.current.push(item);
      productsToAdd.push(item);
    }

    try {
      // Validate subscription intervals
      productsToAdd.forEach((product) => {
        if (product.interval && !isSubscriptionInterval(product.interval)) {
          throw new Error(`Invalid subscription interval: ${product.interval}`);
        }
      });
      if (productsToAdd.length > 0) {
        addToCart(productsToAdd);
      }
    } catch (error) {
      console.error(error);
    } finally {
      const newUrl = new URL(asPath, window.location.origin);
      const keysToDelete = ['add', 'interval', 'addSingle'];
      keysToDelete.map((key) => newUrl.searchParams.delete(key));
      replace(newUrl, undefined, { shallow: true });
    }
  }, [
    isReady,
    query,
    pathname,
    replace,
    addToCart,
    isCartReady,
    add,
    addSingle,
    intervalParam,
    asPath,
  ]);

  /**
   * Awaits for effectiveCart, updates added items and track them
   */
  useEffect(() => {
    if (!effectiveCart || addedVariantIds.current.length === 0) {
      return;
    }

    const foundVariants = effectiveCart.items.filter((item) =>
      addedVariantIds.current.some((variant) => variant.variantId === String(item.variantId))
    );

    if (foundVariants.length > 0) {
      // Track each found variant
      foundVariants.forEach((variant) => {
        const item = {
          productId: String(variant.productId),
          productTitle: variant.productTitle,
          variantTitle: variant.title,
          variantId: String(variant.variantId),
          productType: variant.productType,
          sku: variant.sku,
          price: variant.price,
          tags: variant.tags,
          quantity:
            addedVariantIds.current.find((id) => id.variantId === String(variant.variantId))
              ?.quantity || 1,
          interval: isSubscriptionItem(variant) ? variant.interval : undefined,
        };

        setAddedItems((prev) => [...prev, item]);

        Gtm.track({
          group: Gtm.Group.Product,
          name: Gtm.ProductEvent.AddToCart,
          data: { item },
        });
      });
    }
    // Clear the tracked variant ids to ensure this only runs once
    addedVariantIds.current = [];
  }, [effectiveCart]);

  return {
    addedItems,
  };
};
